<script module>
  import { gql } from '$lib/graphql.svelte.js'

  export const i18n = {
    en: {
      YOUTH: 'youth',
      SENIOR: 'senior',
      ADULT: 'adult',
      '%s ADULT': {
        one: '%s adult',
        other: '%s adults'
      },
      '%s SENIORR': {
        one: '%s senior',
        other: '%s seniors'
      },
      '%s YOUTH': {
        one: '%s youth',
        other: '%s youths'
      },
      '%s people': {
        one: '%s person',
        other: '%s people'
      },
      '%s nights in': {
        one: 'One night in',
        other: '%s nights in'
      },
      '%s changes': {
        one: '%s change',
        other: '%s changes'
      },
      '%s days': {
        one: '%s day',
        other: '%s days'
      }
    },
    sv: {
      YOUTH: 'ungdom',
      SENIOR: 'senior',
      ADULT: 'vuxen',
      '%s ADULT': {
        one: '%s vuxen',
        other: '%s vuxna'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniorer'
      },
      '%s YOUTH': {
        one: '%s ungdom',
        other: '%s ungdomar'
      },
      '%s people': {
        one: '%s person',
        other: '%s personer'
      },
      '%s nights in': {
        one: 'En natt i',
        other: '%s nätter i'
      },
      '%s changes': {
        one: '%s byte',
        other: '%s byten'
      },
      '%s days': {
        one: '%s dag',
        other: '%s dagar'
      },
      'Tickets are typically not available this far ahead in time':
        'Biljetter är vanligtvis inte tillgängliga så långt fram i tiden',
      'Could not find journey in timetable':
        'Kunde inte hitta resan i tidtabellen',
      'Tickets are not available': 'Biljetterna är inte tillgängliga',
      'Too close to departure, try a later date':
        'För nära avresa, försök med ett senare datum',
      'Looking for trains': 'Söker efter tåg',
      'Show details': 'Visa detaljer',
      'Hide details': 'Dölj detaljer',
      'Show price': 'Visa pris',
      Select: 'Välj',
      '2 class': '2 klass',
      '1 class': '1 klass',
      Selected: 'Vald',
      'Travel with a': 'Res med',
      'Night train': 'Nattåg',
      Direct: 'Direkt',
      'Change train': 'Byt tåg',
      'Arrive in': 'Framme i',
      'Run by: %s': 'Körs av: %s',
      'Service: %s': 'Tjänst: %s',
      'Towards: %s': 'Mot: %s',
      Unavailable: 'Ej tillgänglig',
      'Train number: %s': 'Tågnummer: %s',
      'Searching…': 'Söker…',
      'Tickets found': 'Biljetter hittade',
      'Price fr.': 'Pris fr.',
      'Find tickets': 'Hitta biljetter',
      'Looking for tickets': 'Söker biljetter'
    }
  }

  export const JourneyFragment = gql`
    fragment journey on Journey {
      __typename
      id
      status
      itinerary {
        __typename
        ... on Stopover {
          duration
          location {
            uid
            name
            countryCode
            coordinates {
              type
              coordinates
            }
            image {
              url
              alt
            }
          }
        }
        ... on SegmentCollection {
          status
          segments {
            __typename
            duration
            departureAt
            arrivalAt
            transport
            identifier
            direction
            operator {
              name
              image {
                url
              }
            }
            serviceBrand {
              name
              image {
                url
              }
            }
            origin {
              name
              ... on Location {
                uid
                countryCode
              }
            }
            destination {
              name
              ... on Location {
                uid
                countryCode
              }
            }
          }
        }
      }
    }
  `

  export const JourneyWithAvailabilityFragment = gql`
    fragment journeyWithAvailability on Journey {
      ...${JourneyFragment}
      itinerary {
        ... on SegmentCollection {
          availability {
            status
            ticketClasses {
              serviceClass
            }
            priceFrom {
              amount
              currency
            }
          }
        }
      }
    }
  `

  export const JourneyOfferFragment = gql`
    fragment journeyOffer on JourneyOffer {
      ...${JourneyFragment}
      itinerary {
        ... on SegmentCollection {
          offers {
            parts {
              __typename
              ... on AdmissionPart {
                serviceClass
              }
            }
            price {
              amount
              currency
            }
          }
        }
      }
    }
  `

  /** @param {Schema.Journey} journey */
  export function hasNightTrain(journey) {
    return Boolean(
      journey.itinerary
        ?.filter((item) => item.__typename === 'SegmentCollection')
        .flatMap(
          (/** @type {Schema.SegmentCollection} */ item) => item.segments || []
        )
        .some(isNightTrain)
    )
  }

  /** @param {Schema.Segment} segment */
  export function isNightTrain(segment) {
    return segment.transport === 'NIGHT_TRAIN' && segment.duration > 300
  }

  /** @param {Schema.JourneyOffer?} [journey] */
  export function getCheapestPrice(
    journey,
    serviceClasses = ['BASIC', 'STANDARD', 'HIGH', 'BEST', 'UNKNOWN']
  ) {
    if (journey?.status !== 'SUCCESS' || !journey.itinerary) return null

    const offers = journey.itinerary
      .filter((item) => item.__typename === 'SegmentCollection')
      .map((/** @type {Schema.SegmentCollection} */ item) =>
        item.offers?.filter((offer) =>
          offer.parts.every(
            (part) =>
              part.__typename !== 'AdmissionPart' ||
              serviceClasses.includes(/** @type {string} */ (part.serviceClass))
          )
        )
      )
      .reduce((acc, offers) => {
        if (!acc || !offers?.length) return undefined

        acc.push(
          offers.reduce((cheapest, offer) => {
            return offer.price.amount < cheapest.price.amount ? offer : cheapest
          })
        )

        return acc
      }, /** @type {Schema.Offer[]} */ ([]))

    if (!offers?.length) return null

    return offers.reduce(
      (price, offer) => ({
        ...offer.price,
        amount: offer.price.amount + price.amount
      }),
      /** @type {Schema.Money} */ ({ amount: 0 })
    )
  }
</script>

<script>
  import { differenceInMinutes } from 'date-fns'
  import * as Duration from 'duration-fns'
  import { tick } from 'svelte'

  import { register } from '$lib/utils/text.svelte.js'
  import { format } from '$lib/utils/duration.svelte'
  import State from '$lib/utils/state.svelte.js'
  import Tooltip from '$lib/Tooltip.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import Image from '$lib/Image.svelte'
  import { money } from '$lib/money.js'

  let text = register(i18n)

  /** @type {{
   * journey?: Schema.Journey?,
   * origin?: Schema.Location?,
   * destination?: Schema.Location?,
   * passengers?: Schema.PassengerPlaceholderInput[],
   * isPassholder?: boolean,
   * withOffer?: boolean,
   * expanded?: boolean,
   * selected?: boolean,
   * disabled?: boolean,
   * action?: Svelte.Snippet<[Schema.Journey, App.GraphQLOperationResult<{ journey: Schema.JourneyOffer }>, { selected: boolean }]>,
   * onselect?: function(Schema.JourneyOffer): boolean|void
   * }}*/
  let {
    journey = null,
    // origin = null,
    destination = null,
    isPassholder = false,
    selected = false,
    withOffer = false,
    expanded = $bindable(false),
    passengers,
    disabled,
    action,
    onselect
  } = $props()

  const FIRST_CLASS_SERVICES = ['HIGH', 'BEST']
  const SECOND_CLASS_SERVICES = ['BASIC', 'STANDARD']

  /** @type {App.GraphQLOperation<{ journey: Schema.JourneyOffer }, { id: string, currency: string, passengers: Schema.PassengerPlaceholderInput[], isPassholder: boolean }>} */
  const GetJourneyOffer = gql`
    subscription GetJourneyOffer(
      $id: ID!
      $currency: String
      $passengers: [PassengerPlaceholderInput!]!
      $isPassholder: Boolean = false
    ) {
      journey: getJourneyOffer(
        journey: $id
        currency: $currency
        passengers: $passengers
        isPassholder: $isPassholder
      ) {
        ...${JourneyOfferFragment}
      }
    }
  `

  let getOffer = $state(withOffer)

  // Since the entire journey object may change we need to pluck properties that are tracked
  let journeyId = $derived(journey?.id)
  let hasCompleteItinerary = $derived(
    journey?.itinerary?.every(
      (item) => item.__typename !== 'SegmentCollection' || item.segments
    )
  )

  $effect(() => {
    if (journeyId && hasCompleteItinerary && getOffer && passengers) {
      return GetJourneyOffer.execute({
        currency: State.currency,
        id: journeyId,
        passengers,
        isPassholder
      })
    }
  })

  let itinerary = $derived(
    GetJourneyOffer.data?.journey?.itinerary ||
      journey?.itinerary?.map((item) => {
        if (item.__typename !== 'SegmentCollection') return item
        return { ...item, status: getOffer ? 'LOADING' : item.status }
      })
  )

  let isSoldOut = $derived(
    journey?.itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .map((/** @type {Schema.SegmentCollection} */ item) => item.availability)
      .some((availability) => availability?.status === 'SOLD_OUT')
  )

  let priceFrom = $derived(
    journey?.itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .reduce((acc, item, index) => {
        if (!acc && index) return null

        const { availability } = /** @type {Schema.SegmentCollection} */ (item)
        if (!availability?.priceFrom) return null

        const amount = acc ? acc.amount : 0
        return {
          ...availability.priceFrom,
          amount: availability.priceFrom.amount + amount
        }
      }, /** @type {Schema.Money?} */ (null))
  )

  let content
  const toggle = async (event) => {
    event.preventDefault()

    const before = content.getBoundingClientRect()
    expanded = !expanded
    await tick()
    const after = content.getBoundingClientRect()

    content.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    })

    content.animate(
      [{ height: `${before.height}px` }, { height: `${after.height}px` }],
      {
        duration: 400,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    )
  }

  const asTimestamp = $derived.by(() => (date) => {
    return new Date(date).toLocaleTimeString(State.language, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
  })

  const isSameDay = (a, b) => {
    if (!a || !b) return false
    const [aDate] = a.split('T')
    const [bDate] = b.split('T')
    return aDate === bDate
  }

  const getStatusMessage = (status, segment) => {
    switch (status) {
      case 'ONE_DAY':
        return text`Too close to departure, try a later date`
      case 'NO_FARES':
      case 'NO_TIMETABLE': {
        if (segment) {
          const departureAt = new Date(segment.departureAt)
          const threeMonths = Date.now() + 1000 * 60 * 60 * 24 * 90
          if (+departureAt > +threeMonths) {
            return text`Tickets are typically not available this far ahead in time`
          }
        }
        return status === 'NO_TIMETABLE'
          ? text`Could not find journey in timetable`
          : text`Tickets are not available`
      }
      case 'UNKNOWN':
      case 'INDETERMINATE':
      case 'UNABLE_TO_SELL':
      default:
        return text`Tickets are not available`
    }
  }
</script>

{#snippet header(/** @type {Schema.Journey?} */ journey)}
  {@const segments = journey?.itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.segments)
    .filter(Boolean)}
  <div class="header">
    {#if !segments?.every(Boolean)}
      <span class="time u-loading" style:width="12ch">&nbsp;</span>
    {:else if segments.length}
      <span class="time">
        <time datetime={segments.at(0).departureAt}>
          {asTimestamp(segments.at(0).departureAt)}
        </time>
        <span class="divider">–</span>
        <time datetime={segments.at(-1).arrivalAt}>
          {asTimestamp(segments.at(-1).arrivalAt)}
        </time>
      </span>
    {/if}
    {#if journey}
      <button class="toggle" type="button" onclick={toggle}>
        <span class="chevron">
          <Symbol name="chevron" size="sm" />
        </span>
        {expanded ? text`Hide details` : text`Show details`}
      </button>
    {/if}
  </div>
{/snippet}

{#snippet summary(/** @type {Schema.Journey?} */ journey)}
  {@const highlights = journey?.itinerary?.filter(
    (item) =>
      item.__typename === 'Stopover' ||
      (item.__typename === 'SegmentCollection' &&
        item.segments?.some(isNightTrain))
  )}
  {@const stopovers = highlights?.filter(
    (item) => item.__typename === 'Stopover'
  )}
  {@const segments = journey?.itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.segments)
    .filter(Boolean)}
  {@const isLoading = !segments?.every(Boolean)}
  {@const operators = segments?.reduce((operators, segment) => {
    if (
      operators.every((operator) => operator.name !== segment.operator.name)
    ) {
      operators.push(segment.operator)
    }
    return operators
  }, /** @type {Schema.Operator[]} */ ([]))}
  {#if journey?.itinerary?.length && highlights?.length}
    <ol class="highlights">
      {#each highlights as item}
        <li class="highlight">
          <div
            class="thumbnail"
            class:dark={item.__typename === 'SegmentCollection'}>
            {#if item.__typename === 'Stopover'}
              {#if item.location.image}
                <Image
                  src={item.location.image.url}
                  alt={item.location.image.alt || item.location.name}
                  aspect={1}
                  transforms="q_50"
                  variants={[76]}
                  class="img" />
              {:else}
                <Symbol name="pin" />
              {/if}
            {/if}
            {#if item.__typename === 'SegmentCollection'}
              <svg
                class="img"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 23 19">
                <circle cx="20.5" cy="9.5" r=".5" fill="#FC3" />
                <circle cx=".5" cy="1.5" r=".5" fill="#FC3" />
                <circle cx="22.5" cy="5.5" r=".5" fill="#FC3" />
                <circle cx="14.5" cy=".5" r=".5" fill="#FC3" />
                <circle cx="8.5" cy="3.5" r=".5" fill="#FC3" />
                <circle cx="17" cy="5" r="1" fill="#FC3" />
                <circle cx="1.5" cy="6.5" r=".5" fill="#FC3" />
                <path
                  fill="#FC3"
                  d="M6 12.1c0 3.4 2.8 6.2 6.2 6.2 2.2 0 3-.5 4.5-1.9.5-.5.6-1 0-1-1 0-3.7-.6-5-2.8-1.3-2.2-.2-5.1.3-6.1 0-.3 0-.5-.3-.5A6.1 6.1 0 0 0 6 12.1Z" />
              </svg>
            {/if}
          </div>
          <div class="truncate">
            {#if item.__typename === 'Stopover'}
              {@const index = journey.itinerary.indexOf(item) || 0}
              {@const nights = journey.itinerary
                .slice(0, index + 1)
                .reduce(
                  (acc, item) =>
                    item.__typename === 'Stopover'
                      ? acc + Duration.parse(item.duration).days
                      : acc,
                  0
                )}
              {nights ? text`${nights} nights in` : text`Passing through`}
              <br />
              <strong>{item.location.name}</strong>
            {/if}
            {#if item.__typename === 'SegmentCollection'}
              {text`Travel with a`}
              <br />
              <strong>{text`Night train`}</strong>
            {/if}
          </div>
        </li>
      {/each}
    </ol>
  {/if}
  <div class="details">
    <ol class="pills">
      <li class="pill" class:u-loading={isLoading}>
        {#if isLoading}
          <div style:width="5ch">&nbsp;</div>
        {:else}
          {@const duration = journey?.itinerary?.reduce((acc, item) => {
            if (item.__typename === 'Stopover') {
              return acc + Duration.toMinutes(Duration.parse(item.duration))
            }
            if (item.__typename === 'SegmentCollection' && item.segments) {
              return item.segments?.reduce((acc, segment) => {
                return acc + segment.duration
              }, acc)
            }
            return acc
          }, 0)}
          {#if duration}
            {format({ minutes: duration })}
          {/if}
        {/if}
      </li>
      <li class="pill" class:u-loading={isLoading}>
        {#if isLoading}
          <div style:width="7ch">&nbsp;</div>
        {:else if segments && stopovers}
          {segments.length > 1
            ? text`${segments.length - 1} changes`
            : text`Direct`}
        {/if}
      </li>
    </ol>
    {#if operators?.length}
      {@const withImage = operators.filter((item) => item.image).slice(0, 2)}
      <ol class="operators">
        {#each withImage as operator}
          {#if operator.image}
            <li class="operator">
              <img
                src={operator.image.url}
                alt={operator.name}
                class="logotype" />
            </li>
          {/if}
        {/each}
        {#if operators.length > withImage.length}
          <li class="operator">
            +{operators.length - withImage.length}
          </li>
        {/if}
      </ol>
    {/if}
  </div>
{/snippet}

{#snippet highlight(location, label)}
  <div class="highlight frame">
    <div class="thumbnail">
      {#if location.image}
        <Image
          src={location.image.url}
          alt={location.image.alt || location.name}
          aspect={1}
          transforms="q_50"
          variants={[76]}
          class="img" />
      {:else}
        <Symbol name="pin" />
      {/if}
    </div>
    <div class="truncate">
      {label}
      <br />
      <strong>{location.name}</strong>
    </div>
  </div>
{/snippet}

{#snippet leg({
  /** @type {Schema.Segment} */ segment,
  /** @type {Schema.ItineraryItem} */ prev,
  /** @type {Schema.SegmentCollectionStatus} */ status
})}
  {@const prevSegment =
    prev?.__typename === 'SegmentCollection' ? prev.segments?.at(-1) : null}
  {#if !prev || prev.__typename === 'Stopover' || !isSameDay(prevSegment?.departureAt, segment.departureAt)}
    <time class="day" datetime={segment.departureAt}>
      {new Date(segment.departureAt).toLocaleDateString(State.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      })}
    </time>
  {:else if prevSegment}
    {@const duration = differenceInMinutes(
      new Date(segment.departureAt),
      new Date(prevSegment.arrivalAt)
    )}
    <span class="change">
      {text`Change train`} ({format({ minutes: duration })})
    </span>
  {/if}
  <div class="segment frame" class:nighttrain={isNightTrain(segment)}>
    {#if getOffer}
      {@const statusClass = status
        .replace(/^(?!SUCCESS$|LOADING$).*/, 'error')
        .toLowerCase()}
      <span class="status {statusClass}">
        <Tooltip
          text={status === 'LOADING'
            ? text`Searching…`
            : status === 'SUCCESS'
              ? text`Tickets found`
              : getStatusMessage(status, segment)}>
          {#if status === 'LOADING'}
            <Symbol name="spinner" size="md" circle />
          {:else if status === 'SUCCESS'}
            <Symbol name="checkmark" size="md" circle />
          {:else}
            <Symbol name="question" size="md" circle />
          {/if}
        </Tooltip>
      </span>
    {/if}
    <ol>
      <li class="row">
        <time datetime={segment.departureAt} class="time">
          {asTimestamp(segment.departureAt)}
        </time>
        {segment.origin.name}
      </li>
      <li class="row">
        <span class="duration">
          {format({ minutes: segment.duration })}
        </span>
        <ul class="train">
          <li class="info">
            <Tooltip
              text={text`Train number: ${segment.identifier?.replace(/^(\d)/, '#$1')}`}>
              {segment.identifier?.replace(/^(\d)/, '#$1')}
            </Tooltip>
          </li>
          <li class="info">
            {#if segment.operator.image}
              <Tooltip text={text`Run by: ${segment.operator.name}`}>
                <img
                  class="logotype"
                  src={segment.operator.image.url}
                  alt={segment.operator.name} />
              </Tooltip>
            {:else}
              {segment.operator.name}
            {/if}
          </li>
          {#if segment.serviceBrand}
            <li class="info">
              {#if segment.serviceBrand.image}
                <Tooltip text={text`Service: ${segment.serviceBrand.name}`}>
                  <img
                    class="logotype"
                    src={segment.serviceBrand.image.url}
                    alt={segment.serviceBrand.name} />
                </Tooltip>
              {:else}
                {segment.serviceBrand.name}
              {/if}
            </li>
          {/if}
          {#if segment.direction}
            <li class="info shrink">
              <Tooltip text={text`Towards: ${segment.direction}`}>
                <span class="direction">
                  <Symbol name="arrow" size="xs" />
                  {segment.direction}
                </span>
              </Tooltip>
            </li>
          {/if}
        </ul>
      </li>
      <li class="row">
        <time datetime={segment.arrivalAt} class="time">
          {asTimestamp(segment.arrivalAt)}
        </time>
        {segment.destination.name}
      </li>
    </ol>
  </div>
{/snippet}

{#snippet sidebar(
  /** @type {Schema.Journey?} */ journey,
  /** @type {Schema.JourneyOffer|undefined} */ journeyOffer
)}
  {@const secondClassPrice = getCheapestPrice(
    journeyOffer,
    SECOND_CLASS_SERVICES
  )}
  {@const firstClassPrice = getCheapestPrice(
    journeyOffer,
    FIRST_CLASS_SERVICES
  )}
  {@const unknownClassPrice = getCheapestPrice(journeyOffer, ['UNKNOWN'])}
  {@const ticketClasses = journey?.itinerary
    ?.filter((item) => item.__typename === 'SegmentCollection')
    ?.flatMap(
      (/** @type {Schema.SegmentCollection} */ item) =>
        item.availability?.ticketClasses
    )}
  {@const secondClassAvailability =
    Boolean(secondClassPrice) ||
    !ticketClasses?.length ||
    ticketClasses?.some(
      (ticketClass) =>
        !ticketClass || SECOND_CLASS_SERVICES.includes(ticketClass.serviceClass)
    )}
  {@const firstClassAvailability =
    Boolean(firstClassPrice) ||
    !ticketClasses?.length ||
    ticketClasses?.some(
      (ticketClass) =>
        !ticketClass || FIRST_CLASS_SERVICES.includes(ticketClass.serviceClass)
    )}
  {@const unavailable = isSoldOut || journeyOffer?.status === 'ERROR'}
  {@const isLoading = getOffer
    ? !journeyOffer || journeyOffer?.status === 'LOADING'
    : journey?.status === 'LOADING'}

  {#snippet classPrice(price)}
    {#if unavailable}
      <span class="label muted">{text`Unavailable`}</span>
    {:else if isLoading}
      <div class="dots">
        <Symbol name="dots" size="fill" />
      </div>
    {:else if price}
      <span class="price">{money(price)}</span>
    {/if}
  {/snippet}

  {#snippet uncertainClassPrice(price)}
    <div class="shared">
      <span class="label">{text`Price fr.`}</span>
      <span class="price">{money(price)}</span>
    </div>
  {/snippet}

  <div class="sidebar">
    <div class="overview">
      <div class="classes">
        <div class="class">
          <span
            class="label"
            class:muted={!secondClassPrice && !secondClassAvailability}>
            {text`2 class`}
          </span>
          {@render classPrice(secondClassPrice)}
        </div>
        <div class="class">
          <span
            class="label"
            class:muted={!firstClassPrice && !firstClassAvailability}>
            {text`1 class`}
          </span>
          {@render classPrice(firstClassPrice)}
        </div>
      </div>
      {#if priceFrom && !getOffer}
        {@render uncertainClassPrice(priceFrom)}
      {/if}
      {#if !secondClassPrice && !firstClassPrice && unknownClassPrice}
        {@render uncertainClassPrice(unknownClassPrice)}
      {/if}
    </div>
    {#if isSoldOut || !journey || !hasCompleteItinerary || (getOffer && journeyOffer?.status !== 'SUCCESS')}
      <span class="action disabled">
        {text`Find tickets`}
      </span>
    {:else if journey}
      <button
        class="action"
        class:disabled
        class:primary={journeyOffer?.status === 'SUCCESS'}
        name={journeyOffer ? 'journeyOfferId' : 'journeyId'}
        value={journeyOffer?.id || journey.id}
        type="submit"
        onclick={(event) => {
          if (!getOffer) {
            getOffer = true
            event.preventDefault()
          } else if (
            journeyOffer?.status === 'SUCCESS' &&
            typeof onselect === 'function'
          ) {
            if (!onselect(journeyOffer)) {
              event.preventDefault()
            }
          }
        }}>
        {#if action}
          {@render action(journey, GetJourneyOffer, { selected })}
        {:else if selected}
          {text`Selected`}
        {:else if journeyOffer?.status === 'SUCCESS'}
          {text`Select`}
        {:else if GetJourneyOffer.fetching}
          {text`Looking for tickets`}
        {:else}
          {text`Find tickets`}
        {/if}
      </button>
    {/if}
  </div>
{/snippet}

<article
  class="journey"
  class:selected
  class:expanded
  data-journey={journey?.id}>
  <div class="container">
    <div bind:this={content} class="content">
      {@render header(GetJourneyOffer.data?.journey || journey)}
      {#if expanded && itinerary}
        <ol class="items">
          {#each itinerary as item, index (`${item.__typename}-${index}`)}
            {@const prev = itinerary[index - 1]}
            {#if item.__typename === 'Stopover'}
              <li class="item">
                {@render highlight(
                  item.location,
                  format({
                    minutes: Duration.toMinutes(Duration.parse(item.duration))
                  })
                )}
              </li>
            {:else if item.__typename === 'SegmentCollection' && item.segments}
              {#each item.segments as segment}
                <li class="item">
                  {@render leg({ segment, prev, status: item.status })}
                </li>
              {/each}
            {/if}
          {/each}
          {#if destination}
            <li class="item">
              {@render highlight(destination, text`Arrive in`)}
            </li>
          {/if}
        </ol>
      {:else}
        {@render summary(GetJourneyOffer.data?.journey || journey)}
      {/if}
    </div>
    {@render sidebar(journey, GetJourneyOffer.data?.journey)}
  </div>
</article>

<style>
  .journey {
    container-type: inline-size;
    position: relative;
    color: rgb(var(--rgb-text));
    transition: var(--box-shadow-transition);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
    font-size: 1rem;
  }

  .journey.selected::after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    pointer-events: none;
    border: 3px solid rgb(var(--rgb-bright-green));
    border-radius: var(--border-radius-lg);
  }

  .container {
    display: flex;
    flex-direction: column;
  }

  @container (width >= 35rem) {
    .container {
      display: flex;
      flex-direction: row;
    }
  }

  .content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem 1rem 1rem;
    position: relative;
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    background: var(--background);
  }

  .journey.expanded .content {
    gap: 0.5rem;
  }

  @container (width >= 35rem) {
    .content {
      border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
    }
  }

  .sidebar {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    gap: 1.75rem;
    padding: 1rem 1rem 1rem 1.5rem;
    position: relative;
    background: var(--background);
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    font-size: 0.9375rem;
    border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  }

  @container (width >= 35rem) {
    .sidebar {
      flex: 0 0 14rem;
      border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
      border-top: 0;
      border-left: 1px solid rgba(var(--rgb-muted), 0.2);
    }
  }

  .overview {
    width: 100%;
    display: flex;
    align-items: baseline;
  }

  .classes {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  .class {
    display: flex;
    justify-content: space-between;
    height: 1.25rem;
  }

  .class .label {
    padding-top: 0.35rem;
  }

  .class + .class {
    border-top: 1px solid rgba(var(--rgb-muted), 0.2);
    padding-top: 0.375rem;
  }

  .shared {
    display: flex;
    flex-wrap: wrap;
    justify-content: end;
    gap: 0.1875rem;
    padding-left: 2rem;
  }

  .label {
    font-weight: 500;
    font-size: 0.8125rem;
    line-height: 1;
    white-space: nowrap;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .label.muted {
    color: rgba(var(--rgb-muted), 0.6);
  }

  .classes:has(.muted) .label:not(.muted) {
    font-weight: 600;
  }

  .dots {
    width: 1.4rem;
    height: 1.5rem;
  }

  .price {
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1;
    text-align: right;
    letter-spacing: -0.04em;
  }

  /** Header */

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(var(--rgb-gray), 0.2);
  }

  .time {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    font-weight: bold;
    line-height: 1;
  }

  .divider {
    font-weight: 400;
    margin: 0 0.25rem;
  }

  .toggle {
    display: flex;
    align-items: center;
    cursor: default;
    gap: 0.15rem;
    user-select: none;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
    position: relative;
    top: 0.125rem;
  }

  @media (hover) {
    .toggle {
      opacity: 0;
      transition: opacity 100ms var(--ease-in);
    }
  }

  .journey:hover .toggle,
  .journey.expanded .toggle {
    opacity: 1;
    transition: opacity 150ms var(--ease-out);
  }

  .journey:not(.expanded) .toggle::before {
    content: '';
    width: 100%;
    height: 100;
    position: absolute;
    inset: 0;
  }

  @media (scripting: none) {
    .toggle {
      display: none;
    }
  }

  .chevron {
    transform-origin: center;
  }

  .journey.expanded .chevron {
    transform: rotate(180deg);
  }

  /** Highlights */

  .highlights {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    white-space: nowrap;
  }

  .highlight {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    line-height: 1.15;
  }

  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /** Thumbnail */

  .thumbnail {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 2.375rem;
    height: 2.375rem;
    overflow: hidden;
    border-radius: var(--border-radius-xs);
    background-color: var(--background-alt);
  }

  .thumbnail.dark {
    background: rgba(var(--rgb-blue));
  }

  .thumbnail :global(.img) {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  /** Details */

  .details {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /** Pills */

  .pills {
    display: flex;
    gap: 0.5rem;
  }

  .pill {
    padding: 0.25rem 0.75rem 0.2rem;
    white-space: nowrap;
    background: var(--background-alt);
    border-radius: var(--border-radius-full);
    color: rgb(var(--tgb-text));
    font-weight: 600;
    font-size: 0.875rem;
  }

  .operators {
    display: flex;
    gap: 0.85rem;
    align-items: center;
    margin-bottom: 0.55rem;
  }

  @container (width < 25rem) {
    .operators {
      display: none;
    }
  }

  .operator {
    font-weight: bold;
    font-size: 0.875rem;
  }

  .logotype {
    flex: 0 0 auto;
    max-height: 1.25rem;
    width: auto;
  }

  .direction {
    display: inline-flex;
    align-items: center;
    font-size: 0.875rem;
    font-weight: normal;
    color: rgb(var(--rgb-muted));
  }

  /** Actions */

  .option {
    display: contents;
  }

  .availability {
    color: rgb(var(--rgb-muted));
  }

  .action {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 2.5rem;
    width: 100%;
    padding: 0.6rem 1rem;
    border: transparent;
    background: transparent;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    cursor: pointer;
    user-select: none;
    font-weight: 600;
    font-size: 1rem;
    border-radius: var(--border-radius-full);
    transition: var(--box-shadow-transition);
    box-shadow: var(--box-shadow-sm);
  }

  .action:not(.disabled):hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .action:not(.disabled):active {
    box-shadow: var(--box-shadow-sm-active);
    transition: var(--box-shadow-active-transition);
  }

  .action.primary {
    background: rgb(var(--rgb-success, var(--rgb-green)));
    color: rgb(var(--rgb-white));
    font-weight: 600;
  }

  .journey.selected .action {
    background: rgb(var(--rgb-green));
    color: rgb(var(--rgb-white));
  }

  :global(.using-keyboard) .journey.selected .action {
    outline: solid var(--current-focus-ring-width, var(--focus-ring-width));
    outline-color: rgb(
      var(--current-focus-ring-color, var(--focus-ring-color))
    );
  }

  .action.disabled {
    background: transparent;
    color: rgba(var(--rgb-muted), 0.6);
    border: 1px solid rgba(var(--rgb-muted), 0.25);
    cursor: default;
    font-weight: 400;
    box-shadow: none;
  }

  /** Error */

  .message {
    margin: 0 auto;
    text-align: left;
    color: rgb(var(--rgb-muted));
  }

  /** Loader */

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    font-size: 0.75rem;
    font-weight: bold;
    text-align: center;
    white-space: nowrap;
    color: rgb(var(--rgb-muted));
  }

  @container (width >= 35rem) {
    .loader {
      flex-direction: column;
    }
  }

  /** Items */

  .items {
    display: grid;
    gap: 0.5rem;
  }

  /** Frame */

  .frame {
    position: relative;
    padding: 0.75rem;
    border: 1px solid rgba(var(--rgb-muted), 0.25);
    border-radius: var(--border-radius-sm);
  }

  /** Segment */

  .segment {
    font-weight: 600;
    white-space: nowrap;
  }

  .day {
    display: block;
    font-weight: 600;
    margin: 0.5rem 0;
  }

  .change {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  .status {
    --current-color: rgb(var(--rgb-text));
    --current-background: rgba(var(--rgb-muted), 0.2);

    display: block;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 1;
  }

  .status.error {
    --current-color: rgba(var(--rgb-muted));
    --current-background: rgba(var(--rgb-light-gray), 0.4);
  }

  .status.success {
    --current-color: rgb(var(--rgb-green));
    --current-background: rgb(var(--rgb-bright-green), 0.2);
  }

  .row {
    display: grid;
    grid-template-columns: 5rem auto;
    align-items: center;
    gap: 0.75rem;
    padding: 0.2rem 0;
    position: relative;
  }

  .row::before {
    content: '';
    width: 1.5rem;
    height: 100%;
    position: absolute;
    left: 3.5rem;
    top: 0;
    background-color: rgb(var(--rgb-highlight));
  }

  .segment.nighttrain .row::before {
    background-color: rgb(var(--rgb-blue));
  }

  .row:first-child::before {
    border-radius: var(--border-radius-full) var(--border-radius-full) 0 0;
  }

  .row:last-child::before {
    border-radius: 0 0 var(--border-radius-full) var(--border-radius-full);
  }

  .segment .time {
    display: block;
    padding-right: 2.25rem;
    position: relative;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .segment .time::after {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    position: absolute;
    top: 50%;
    right: 0.75rem;
    border-radius: 100%;
    background: var(--background);
    transform: translate(50%, -50%);
  }

  .duration {
    padding-right: 2.25rem;
    font-weight: normal;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
  }

  .train {
    display: flex;
    align-items: center;
    gap: 0.25rem 0.5rem;
    flex-wrap: wrap;
    white-space: nowrap;
    font-weight: normal;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
  }

  .info {
    flex: 0 0 auto;
  }

  .info.shrink {
    flex-shrink: 1;
  }

  .train .logotype {
    height: 1.1rem;
  }
</style>
